<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <template v-slot:header>
                <h5>Persetujuan Catatan Kas Harian</h5>
            </template>

            <div class="detail-data-wrapper">
                <b-table
                    bordered
                    hover
                    show-empty
                    responsive
                    class="mb-3"
                    :busy="isBusy"
                    :items="items"
                    :fields="fields"
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading data...</strong>
                        </div>
                    </template>
                    <template v-slot:empty>
                        Tidak ada data Persetujuan Catatan Kas Harian.
                    </template>
                    <template v-slot:cell(tanggal)="row">
                        <div>{{ $helper.changeDateFormat(row.item.tanggal) }}</div>
                    </template>
                    <template v-slot:cell(cabang)="row">
                        <div>{{ row.item.cabangName }}</div>
                    </template>
                    <template v-slot:cell(informasiKas)="row">
                        <div class="font-weight-bold">
                            Sistem Kas Besar: Rp. {{ $helper.addThousandSeparator(row.item.saldoKasSistem) }}
                        </div>
                        <div class="smallText text-muted">
                            Fisik Kas Besar: Rp. {{ $helper.addThousandSeparator(row.item.saldoKasFisik) }}
                        </div>
                        <div class="smallText" :class="getSelisihClass(row.item.selisih)">
                            Selisih Kas Besar: Rp. {{ $helper.addThousandSeparator(Math.abs(row.item.selisih)) }}
                        </div>
                        <div class="smallText font-weight-bold border-top mt-1 pt-1">
                            Sistem Kas Kecil: Rp. {{ $helper.addThousandSeparator(row.item.saldoKasKecilSistem) }}
                        </div>
                        <div class="smallText text-muted">
                            Fisik Kas Kecil: Rp. {{ $helper.addThousandSeparator(row.item.saldoKasKecilFisik) }}
                        </div>
                        <div class="smallText" :class="getSelisihClass(row.item.selisihKasKecil)">
                            Selisih Kas Kecil: Rp. {{ $helper.addThousandSeparator(Math.abs(row.item.selisihKasKecil)) }}
                        </div>
                    </template>
                    <template v-slot:cell(informasiPetugas)="row">
                        <div class="smallText">
                            <strong>Dibuat Oleh:</strong> {{ row.item.createdBy || "-" }}
                        </div>
                        <div class="smallText">
                            <strong>Staff Opname:</strong> {{ row.item.staffOpname || "-" }}
                        </div>
                        <div class="smallText">
                            <strong>Diverifikasi Staff Opname Pada:</strong> {{ row.item.approvedAt || "-" }}
                        </div>
                        <template v-if="isCancelledStatus(row.item.saksiStatus)">
                            <div class="smallText">
                                <strong>Dibatalkan Oleh:</strong> {{ row.item.rejectedBy || "-" }}
                            </div>
                            <div class="smallText">
                                <strong>Dibatalkan Pada:</strong> {{ row.item.rejectedAt || "-" }}
                            </div>
                            <div class="smallText">
                                <strong>Alasan Dibatalkan:</strong> {{ row.item.rejectedNote || "-" }}
                            </div>
                        </template>
                    </template>
                    <template v-slot:cell(status)="row">
                        <span :class="getStatusClass(getStaffStatus(row.item))">
                            {{ getStatusLabel(getStaffStatus(row.item)) }}
                        </span>
                    </template>
                    <template v-slot:cell(action)="row">
                        <div class="d-flex flex-wrap gap-2" v-if="getStaffStatus(row.item) == STAFF_STATUS.PENDING">
                            <b-button
                                size="sm"
                                variant="success"
                                v-b-tooltip.hover
                                title="Setujui"
                                @click="onShowApprove(row.item)"
                            >
                                <i class="fa fa-check"></i>
                            </b-button>
                            <b-button
                                size="sm"
                                variant="danger"
                                v-b-tooltip.hover
                                title="Batalkan"
                                @click="onShowCancel(row.item)"
                            >
                                <i class="fa fa-times"></i>
                            </b-button>
                        </div>
                    </template>
                </b-table>

                <div class="pagination-wrapper d-flex align-items-center justify-content-between">
                    <b-input-group prepend="Tampilkan" class="align-items-center tableShowItem">
                        <b-form-select v-model="show" @change="onLimitChange" :options="optionsLimit">
                            <template v-slot:first>
                                <b-form-select-option :value="null" disabled />
                            </template>
                        </b-form-select>
                        <span class="ml-2">{{ totalViewText }}</span>
                    </b-input-group>
                    <b-pagination
                        class="mb-0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        aria-controls="approval-catatan-kas-table"
                        @input="onPageChange"
                    />
                </div>
            </div>
        </b-card>

        <StatementConfirmModal
            id="modal-approve-catatan-kas"
            title="Konfirmasi Persetujuan Catatan Kas Harian"
            :is-submitting="isLoadingApprove"
            @submit="onApprove"
        >
            <p>Apakah Anda yakin ingin menyetujui hasil pemeriksaan catatan kas ini?</p>
            <template v-slot:statement>
                <p class="mb-2">
                    Saya menyatakan bahwa saya telah memeriksa seluruh data dan informasi catatan kas ini sesuai dengan kondisi sebenarnya.
                    Apabila ditemukan <strong>ketidaksesuaian</strong>, saya bersedia menerima
                    <strong>konsekuensi dan tindakan</strong> sesuai dengan ketentuan yang berlaku.
                </p>
            </template>
        </StatementConfirmModal>

        <StatementConfirmModal
            id="modal-confirm-cancel-catatan-kas"
            title="Konfirmasi Batalkan Catatan Kas"
            :is-submitting="isLoadingCancel"
            ok-variant="danger"
            @submit="cancelCatatanKas"
        >
            <p>Apakah Anda yakin ingin membatalkan catatan kas ini?</p>
            <b-form-group label="Alasan">
                <b-form-textarea
                    v-model="cancelReason"
                    maxlength="100"
                    rows="3"
                    max-rows="3"
                    placeholder="Masukkan alasan pembatalan"
                />
            </b-form-group>
            <template v-slot:statement>
                <p class="mb-2">
                    Saya menyatakan bahwa alasan pembatalan yang saya input telah sesuai dengan kondisi sebenarnya.
                    Apabila ditemukan <strong>ketidaksesuaian</strong>, saya bersedia menerima
                    <strong>konsekuensi dan tindakan</strong> sesuai dengan ketentuan yang berlaku.
                </p>
            </template>
        </StatementConfirmModal>

    </div>
</template>

<script>
import CONSTANTS from '@/constants'
import StatementConfirmModal from "@/components/common/StatementConfirmModal"

const STAFF_STATUS = {
    PENDING: 0,
    APPROVE: 1,
    REJECT: 2,
}

export default {
    name: "approval-catatan-kas",
    components: {
        StatementConfirmModal,
    },
    data() {
        return {
            STAFF_STATUS,
            initStatus: 0,
            isBusy: false,
            isLoadingApprove: false,
            isLoadingCancel: false,
            selectedRow: null,
            cancelReason: "",
            fields: [
                { key: "tanggal", label: "Tanggal" },
                { key: "cabang", label: "Cabang" },
                { key: "shift", label: "Shift" },
                { key: "informasiKas", label: "Informasi Kas" },
                { key: "informasiPetugas", label: "Informasi Petugas" },
                { key: "status", label: "Status" },
                { key: "action", label: "Aksi" },
            ],
            data: [],
            items: [],
            currentPage: 1,
            show: 10,
            perPage: 10,
            totalRows: 0,
            totalViewText: "",
            optionsLimit: CONSTANTS.OPTIONS_LIMIT_PAGINATION,
        }
    },
    methods: {
        async init() {
            this.initStatus = 0
            try {
                await this.getItems()
                this.initStatus = 1
            } catch (error) {
                this.$helper.parseError(this, error)
                this.initStatus = -1
            }
        },
        getItems(page = 1) {
            this.isBusy = true
            this.items = []
            this.currentPage = isNaN(page) ? 1 : page

            const params = {
                page: this.currentPage - 1,
                size: this.perPage,
            }

            return this.$axios.get("log-book-kasir/get-approval-saksi", { params })
                .then((response) => {
                    const result = response.data || {}
                    const content = Array.isArray(result.content) ? result.content : []

                    this.items = content.map((item) => ({
                        id: item.id,
                        tanggal: item.tanggal,
                        cabangName: item.cabangName || "-",
                        shift: this.getShiftLabel(item.shift),
                        createdBy: this.formatUser(item.createdBy),
                        staffOpname: this.formatUser(item.staff),
                        approvedAt: this.formatDateTime(item.saksiProcessedAt),
                        saldoKasSistem: Number(item.saldoLaciSistem || 0) + Number(item.saldoBrankasSistem || 0),
                        saldoKasFisik: Number(item.saldoLaciFisik || 0) + Number(item.saldoBrankasFisik || 0),
                        saldoKasKecilSistem: Number(item.saldoKasKecilSistem || 0),
                        saldoKasKecilFisik: Number(item.saldoKasKecilFisik || 0),
                        selisih: (
                            Number(item.saldoLaciFisik || 0) + Number(item.saldoBrankasFisik || 0)
                        ) - (
                            Number(item.saldoLaciSistem || 0) + Number(item.saldoBrankasSistem || 0)
                        ),
                        selisihKasKecil: Number(item.selisihKasKecil || 0),
                        saksiStatus: item.saksiStatus,
                        catatan: item.note || "",
                        rejectedBy: item.rejectedBy || "",
                        rejectedAt: this.formatDateTime(item.rejectedAt),
                        rejectedNote: item.rejectedNote || "",
                    }))

                    this.totalRows = Number(result.totalElements || 0)
                    this.updateTotalViewText()
                })
                .catch((error) => {
                    throw error
                })
                .finally(() => {
                    this.isBusy = false
                })
        },
        updateTotalViewText() {
            if (this.totalRows === 0) {
                this.totalViewText = "0 dari 0 data"
                return
            }

            const prev = (this.currentPage - 1) * this.perPage + 1
            let limit = (this.currentPage - 1) * this.perPage + this.perPage
            if (limit > this.totalRows) limit = this.totalRows
            this.totalViewText = `Menampilkan ${prev} - ${limit} dari ${this.totalRows} entri`
        },
        getSelisihClass(selisih) {
            if (Number(selisih) > 0) return "text-success"
            if (Number(selisih) < 0) return "text-danger"
            return ""
        },
        getShiftLabel(shift) {
            if (Number(shift) === 3) return "Malam"
            if (Number(shift) === 2) return "Siang"
            return "Pagi"
        },
        formatUser(user) {
            if (!user) return "-"
            return `${user.username || ""} - ${user.fullName || ""}`.trim()
        },
        formatDateTime(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-"
            return `${this.$helper.changeDateFormat(value)} ${this.$helper.changeDateFormatGetHour(value)}`
        },
        getStatusLabel(status) {
            if (Number(status) === STAFF_STATUS.APPROVE) return "Disetujui"
            if (Number(status) === STAFF_STATUS.REJECT) return "Dibatalkan"
            return "Pending"
        },
        getStatusClass(status) {
            if (Number(status) === STAFF_STATUS.APPROVE) return "status-box-md status-box-success"
            if (Number(status) === STAFF_STATUS.REJECT) return "status-box-md status-box-danger"
            return "status-box-md status-box-warning"
        },
        getStaffStatus(item) {
            if (item && item.saksiStatus !== undefined && item.saksiStatus !== null) return item.saksiStatus
            return STAFF_STATUS.PENDING
        },
        isCancelledStatus(status) {
            return Number(status) === STAFF_STATUS.REJECT
        },
        onLimitChange(newPerPage) {
            this.perPage = newPerPage
            this.getItems(1)
        },
        onPageChange(newPage) {
            this.getItems(newPage)
        },
        onShowApprove(row) {
            this.selectedRow = row
            this.$bvModal.show("modal-approve-catatan-kas")
        },
        onShowCancel(row) {
            this.selectedRow = row
            this.cancelReason = ""
            this.$bvModal.show("modal-confirm-cancel-catatan-kas")
        },
        onApprove() {
            this.isLoadingApprove = true
            const selectedId = this.selectedRow ? this.selectedRow.id : null

            this.$axios.post(`log-book-kasir/approve-saksi/${selectedId}`)
                .then(() => {
                    this.$helper.toastSucc(this, "Catatan kas berhasil disetujui.")
                    this.$bvModal.hide("modal-approve-catatan-kas")
                    this.getItems(this.currentPage)
                })
                .catch((error) => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isLoadingApprove = false
                })
        },
        cancelCatatanKas() {
            if (this.$helper.isNullOrEmpty(this.cancelReason)) {
                this.$helper.toastErr(this, "Alasan pembatalan wajib diisi.")
                return
            }

            this.isLoadingCancel = true
            const selectedId = this.selectedRow ? this.selectedRow.id : null

            this.$axios.post(`log-book-kasir/reject/${selectedId}`, null, { params: { note: this.cancelReason } })
                .then(() => {
                    this.$helper.toastSucc(this, "Catatan kas berhasil dibatalkan.")
                    this.$bvModal.hide("modal-confirm-cancel-catatan-kas")
                    this.selectedRow = null
                    this.cancelReason = ""
                    this.getItems(this.currentPage)
                })
                .catch((error) => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isLoadingCancel = false
                })
        },
    },
    created() {
        this.init()
    },
}
</script>
